import React, { useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { sanitize } from 'isomorphic-dompurify'
import { Mention } from 'modules/community/components/editor/elements/mention/mention'
import { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import { useHighlightSearchContext } from 'modules/community/context/highlight-search'

const parseTextWithMentions = (text: string, mentions: MentionInterface[]): string => {
  const parts = []
  let lastIndex = 0

  mentions?.forEach(({ userId, displayName, profileImage, positionStart, positionEnd }) => {
    if (positionStart > lastIndex) {
      parts.push(text.substring(lastIndex, positionStart))
    }

    const dataAvatarUrl = profileImage ? `data-avatar-url="${profileImage}"` : ''
    const dataId = userId ? `data-id="${userId}"` : ''

    parts.push(
      `<span data-type="mention" contenteditable="false" ${dataAvatarUrl} data-cy="mention-${displayName}" ${dataId}>@${displayName}</span>`,
    )

    lastIndex = positionEnd
  })

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts.join('')
}

interface TextWithMentionsProps extends React.HTMLProps<HTMLDivElement> {
  text: string
  mentions: MentionInterface[]
  truncateLength?: number
}

export const TextWithMentions = ({
  text,
  mentions,
  truncateLength,
  ...rest
}: TextWithMentionsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const highlightContext = useHighlightSearchContext()

  useEffect(() => {
    if (containerRef.current) {
      const htmlContent = parseTextWithMentions(text, mentions)
      let finalHtml = htmlContent

      if (highlightContext?.highlightKeyword && truncateLength) {
        finalHtml = highlightContext?.wrapWithHighlight(finalHtml, truncateLength)
      }
      if (truncateLength && finalHtml.length > truncateLength) {
        finalHtml = finalHtml.slice(0, truncateLength).slice(0, -4) + '...</p>'
      }
      containerRef.current.innerHTML = finalHtml

      const mentionElements = containerRef.current.querySelectorAll('[data-type="mention"]')
      mentionElements?.forEach(element => {
        const id = element.getAttribute('data-id')
        const displayName = element.getAttribute('data-cy')?.split('mention-').pop()
        const profileImageUrl = element.getAttribute('data-avatar-url')

        const root = createRoot(element)
        root.render(
          <Mention
            element={{
              displayName: displayName || '',
              id: Number(id) || 0,
              profileImageUrl: profileImageUrl || '',
              type: 'mention',
              children: [{ text: '' }],
            }}
          />,
        )
      })
    }
  }, [text, mentions, truncateLength])

  return <div ref={containerRef} {...rest} />
}
